import AbstractListStrategy from "./AbstractListStrategy.js";

/**
 * Strategy to render the items of a list source stream as a table.
 *
 * @param {GridColumnSpecification[]} columnSpecifications - Specifications
 *  for the columns of the table
 * @param {bool} [striped] - Set to true if odd rows should have a
 *   different background
 * @param {bool} [highlightOnHover] - Set to true if the hovered row
 *   should have a different background
 * @param {bool} [bordered] - Set to true if lines should separate
 *   rows and columns
 * @param {Renderer} [tfootRenderer] - If defined, it must be a
 *   function accepting an object with `items` as key. The function
 *   should return a Renderable. The Renderable will be added to a
 *   <tfoot> DOM node
 */
export default class TableStrategy extends AbstractListStrategy {
	constructor({
		columnSpecifications,
		striped = false,
		highlightOnHover = false,
		bordered = false,
		tfootRenderer,
	}) {
		super(...arguments);

		this._columnSpecifications = columnSpecifications;
		this._striped = striped;
		this._highlightOnHover = highlightOnHover;
		this._bordered = bordered;
		this._tfootRenderer = tfootRenderer;

		this._renderedItems = null;
	}

	baseCss() {
		return "tableListWidget";
	}

	renderLoadingOn(html) {
		if (!this._renderedItems) {
			super.renderLoadingOn(html);
			return;
		}

		html.div(
			{
				class: [this.cssClass(), "loading"],
				"data-test": "loader",
			},
			html.div({ class: "click-blocker" }),
			...this._tableRenderer({
				items: this._renderedItems,
				disabled: true,
			}),
		);
	}

	itemsRenderer({ items }) {
		this._renderedItems = items;

		return (html) => {
			html.div(
				{ class: this.cssClass() },
				...this._tableRenderer({
					items,
				}),
			);
		};
	}

	makeItemRenderable({ html, item }) {
		return html.tr(
			{ "data-test": this._dataTestName },
			this._columnSpecifications.map((columnSpec) =>
				this._cellRenderer({ columnSpec, item }),
			),
		);
	}

	_cellRenderer({ columnSpec, item }) {
		return (html) => {
			html.td(
				{
					class: columnSpec.cssClass(item),
					"data-test": columnSpec.getDataTestName(),
				},
				columnSpec.renderer(item),
			);
		};
	}

	_tableRenderer({ items, disabled = false }) {
		return [
			(html) =>
				html.table(
					{
						class: [
							"grid",
							{
								"table-striped": this._striped,
								"table-hover": this._highlightOnHover,
								"table-bordered": this._bordered,
							},
						],
					},
					this._headersRenderer({ disabled }),
					this._bodyRenderer({ items }),
					this._footerRenderer({ items }),
				),
			this._trailingFooterRenderer({ disabled }),
		];
	}

	_headersRenderer() {
		return (html) =>
			html.thead(
				html.tr(
					this._columnSpecifications.map((columnSpec) =>
						this._columnHeaderRenderer({ columnSpec }),
					),
				),
			);
	}

	_bodyRenderer({ items }) {
		return (html) => {
			html.tbody(
				items.map((item, index) =>
					this._listSourceWidget.makeItemRenderable({
						html,
						item,
						index,
					}),
				),
			);
		};
	}

	_footerRenderer({ items }) {
		return (html) => {
			if (!this._tfootRenderer) return;

			html.tfoot(this._tfootRenderer({ items }));
		};
	}

	_columnHeaderRenderer({ columnSpec }) {
		const sortable = columnSpec.isSortable();

		return (html) =>
			html.th(
				{
					class: this._buildHeaderCss({
						columnSpec,
						sortable,
					}),
					click:
						sortable &&
						(() => {
							this._listSourceWidget?.orderBy(
								columnSpec.getSortCriterion(),
							);
						}),
					"data-test": columnSpec.getDataTestName() || "",
				},
				columnSpec.getName(),
				sortable
					? html.i({
							class: [
								"fa",
								"sortable-icon",
								columnSpec.getType(),
							],
						})
					: html.span({ class: "sortable-icon no-icon" }),
			);
	}

	_rowRenderer(item) {
		return (html) => {
			html.tr(
				{ "data-test": this._dataTestName },
				this._columnSpecs.map((columnSpec) =>
					this._cellRenderer({ columnSpec, item }),
				),
			);
		};
	}

	_trailingFooterRenderer({ disabled = false } = {}) {
		if (!this._trailingWidget) {
			return "";
		}

		return (html) => {
			html.div(
				{ class: "table-footer" },
				html.div(
					{
						class: ["trailing-card", { disabled }],
						"data-test": "trailing-item-container",
					},
					this._trailingWidget,
					disabled
						? html.div({ class: "trailing-item-blocker" })
						: "",
				),
			);
		};
	}

	_buildHeaderCss({ columnSpec, sortable }) {
		const ascending = this._listSourceWidget?.getAscendingSort();
		return [
			columnSpec.getHeaderCssClass(),
			{
				sortable,
				active: this._listSourceWidget?.isCurrentSortCriterion(
					columnSpec.getSortCriterion(),
				),
				ascending,
				descending: !ascending,
			},
		];
	}
}
